// Yoga Somerset - County Hub Page
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import Navigation from "../components/shared/Navigation";
import Footer from "../components/shared/Footer";
import PageBreadcrumb from "../components/shared/PageBreadcrumb";
import { usePageMeta, generateLocationSchema, SchemaScript } from "../components/shared/SEOHelper";

const YogaSomerset = () => {
  usePageMeta({
    title: "Yoga Somerset | Yoga Teacher in Somerset | Annelie Lily",
    description: "Yoga classes across Somerset. Small group Hatha & Yin yoga, private sessions, and holistic treatments.",
    canonical: "https://annelielily.com/areas/yoga-somerset"
  });

  const schema = generateLocationSchema({
    townName: "Somerset",
    description: "Yoga classes across Somerset at Annelie Lily Yoga in Frome. Serving Bath, Wells, Shepton Mallet, Radstock and surrounding villages.",
    url: "https://annelielily.com/areas/yoga-somerset"
  });

  const somersetTowns = [
    { name: "Frome", link: "/", desc: "Home base — all classes held here", distance: "0 min", featured: true },
    { name: "Bath", link: "/areas/yoga-bath", desc: "Historic spa city, 25 min drive", distance: "25 min" },
    { name: "Wells", link: "/areas/yoga-wells", desc: "Cathedral city through the Mendips", distance: "25 min" },
    { name: "Shepton Mallet", link: "/areas/yoga-shepton-mallet", desc: "Market town on the A361", distance: "15 min" },
    { name: "Radstock", link: "/areas/yoga-radstock", desc: "Former mining town, easy access", distance: "15 min" },
    { name: "Midsomer Norton", link: "/areas/yoga-midsomer-norton", desc: "Twin town with Radstock", distance: "15 min" },
    { name: "Mells", link: "/areas/yoga-mells", desc: "Beautiful village near Frome", distance: "5 min" },
    { name: "Nunney", link: "/areas/yoga-nunney", desc: "Castle village, very close", distance: "5 min" },
    { name: "Beckington", link: "/areas/yoga-beckington", desc: "On the A36 towards Bath", distance: "10 min" },
  ];

  const services = [
    { name: "Hatha Yoga Bath", link: "/areas/yoga-bath/hatha-yoga", desc: "Traditional practice for Bath residents" },
    { name: "Hatha Yoga Wells", link: "/areas/yoga-wells/hatha-yoga", desc: "Traditional practice for Wells residents" },
    { name: "Yin Yoga Bath", link: "/areas/yoga-bath/yin-yoga", desc: "Deep stretches for Bath residents" },
    { name: "Yin Yoga Radstock", link: "/areas/yoga-radstock/yin-yoga", desc: "Deep stretches for Radstock residents" },
    { name: "Private Sessions", link: "/yoga-classes/private-yoga", desc: "One-to-one yoga tailored to you" },
    { name: "Healing & Treatments", link: "/treatments", desc: "Massage, Reiki, energy healing" },
  ];

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={schema} />
      <Navigation />
      <PageBreadcrumb currentPage="Yoga in Somerset" currentSlug="areas/yoga-somerset" />
      
      {/* Hero */}
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
              Yoga Across Somerset
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">
              Yoga Classes Somerset
            </h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-8">
              Small, welcoming yoga classes based in Frome, serving towns and villages across Somerset. 
              From Bath to Wells, Shepton Mallet to the Mendip villages — quality yoga is closer than you think.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700">
                <MapPin className="w-4 h-4 text-gold" />
                Based in Frome
              </span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700">
                Serving 15+ Somerset towns
              </span>
            </div>
            <Link to="/contact" className="btn-primary">
              Find Classes Near You <ArrowRight className="w-4 h-4 inline ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* About Somerset Coverage */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
            Your Local Yoga Teacher in Somerset
          </h2>
          <div className="prose-content space-y-4 text-neutral-700">
            <p>
              I'm Annelie, and I teach yoga from my studio in <strong>Frome</strong> — right in the heart of Somerset. 
              While my classes are held in Frome, students come from across the county: <Link to="/areas/yoga-bath" className="text-gold hover:underline">Bath</Link>, 
              <Link to="/areas/yoga-wells" className="text-gold hover:underline"> Wells</Link>, <Link to="/areas/yoga-shepton-mallet" className="text-gold hover:underline">Shepton Mallet</Link>, 
              the Mendip villages, and beyond.
            </p>
            <p>
              Somerset is a beautiful county to live in, but finding good yoga can be tricky outside the larger towns. 
              That's why I keep my classes small (6-8 people) and personal — if you're making the journey, 
              it should be worth it.
            </p>
            <p>
              Whether you're looking for <Link to="/areas/yoga-bath/hatha-yoga" className="text-gold hover:underline">Hatha yoga near Bath</Link>, 
              <Link to="/areas/yoga-wells/yin-yoga" className="text-gold hover:underline"> Yin yoga near Wells</Link>, 
              or <Link to="/yoga-classes/private-yoga" className="text-gold hover:underline">private sessions</Link> for focused attention — 
              you'll find a calm, supportive space here.
            </p>
          </div>
        </div>
      </section>

      {/* Somerset Locations Grid */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4 text-center">
            Yoga Classes for Somerset Towns
          </h2>
          <p className="text-neutral-600 text-center mb-10 max-w-2xl mx-auto">
            Click your town to see how to get to classes, what's available, and answers to common questions.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {somersetTowns.map((town, index) => (
              <motion.div
                key={town.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={town.link}
                  className={`block p-5 rounded-xl border transition-all group ${
                    town.featured 
                      ? 'bg-gold/10 border-gold/30 hover:border-gold' 
                      : 'bg-white/70 border-gold/10 hover:border-gold/30'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-heading text-lg text-neutral-800 group-hover:text-gold transition-colors">
                      {town.name}
                    </h3>
                    <span className="text-xs text-neutral-500 bg-neutral-100 px-2 py-1 rounded-full">
                      {town.distance}
                    </span>
                  </div>
                  <p className="text-neutral-600 text-sm mb-2">{town.desc}</p>
                  <span className="text-gold text-sm font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    View details <ArrowRight className="w-3 h-3" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-10 text-center">
            Yoga & Wellness Services in Somerset
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((service, index) => (
              <Link
                key={service.name}
                to={service.link}
                className="block bg-white/70 p-5 rounded-xl border border-gold/10 hover:border-gold/30 transition-all group"
              >
                <h3 className="font-heading text-lg text-neutral-800 mb-2 group-hover:text-gold transition-colors">
                  {service.name}
                </h3>
                <p className="text-neutral-600 text-sm">{service.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
            Ready to Start Yoga in Somerset?
          </h2>
          <p className="text-neutral-600 mb-6 max-w-xl mx-auto">
            Get in touch and I'll help you find the right class. All levels welcome, from complete beginners to experienced practitioners.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary">
              Book a Class <ArrowRight className="w-4 h-4 inline ml-2" />
            </Link>
            <Link to="/yoga-classes/beginner-yoga" className="btn-secondary">
              New to Yoga?
            </Link>
          </div>
        </div>
      </section>

      {/* Wiltshire Link */}
      <section className="py-12 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-neutral-600 text-sm mb-4">Also serving Wiltshire:</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/areas/yoga-wiltshire" className="text-sm text-gold hover:underline font-medium">View Wiltshire locations →</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default YogaSomerset;
